import { api } from './api';

import { IApiKey, IShopDetails } from '@/types';
import { Shop, ShopResponse, GetShopsParams, SwitchShopResponse, ShopMailing } from '@/types';

const PROD_API_URL = 'https://api.ioka.kz';

export const shopApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getAllShops: builder.query<ShopResponse, GetShopsParams>({
      query: (params) => ({
        url: '/v2/shops',
        params,
      }),
      providesTags: (result) =>
        result && result.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'Shop' as const, id })),
              { type: 'Shop', id: 'LIST' },
            ]
          : [{ type: 'Shop', id: 'LIST' }],
    }),
    // Получение списка магазинов
    getShops: builder.query<Shop[], void>({
      query: () => ({
        url: '/v2/auth/shops',
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: 'Shop' as const, id })),
              { type: 'Shop', id: 'LIST' },
            ]
          : [{ type: 'Shop', id: 'LIST' }],
    }),
    // Получение текущего магазина и его дочерних магазинов
    getCurrentShops: builder.query<Shop[], GetShopsParams>({
      query: (params) => ({
        url: '/v2/auth/shops/current',
        params,
      }),
    }),
    //  Получение списка рассылок магазина
    getShopMailingList: builder.query<ShopMailing[], string>({
      query: (shopId) => ({
        url: `/v2/shops/${shopId}/emails`,
      }),
    }),
    //  Создание рассылок магазина
    createShopMailing: builder.mutation<ShopMailing, { shop_id: string; data: ShopMailing }>({
      query: ({ shop_id, data }) => ({
        url: `/v2/shops/${shop_id}/emails`,
        method: 'POST',
        body: data,
      }),
    }),
    //  Редактирование рассылок магазина
    updateShopMailing: builder.mutation<
      void,
      { shopId: string; emailId: string; data: ShopMailing }
    >({
      query: ({ shopId, emailId, data }) => ({
        url: `/v2/shops/${shopId}/emails/${emailId}`,
        method: 'PUT',
        body: data,
      }),
    }),
    //  Удаление рассылок магазина
    deleteShopMailing: builder.mutation<void, { shopId: string; emailId: string }>({
      query: ({ shopId, emailId }) => ({
        url: `/v2/shops/${shopId}/emails/${emailId}`,
        method: 'DELETE',
      }),
    }),
    // Эндпоинт для переключения магазина
    switchShop: builder.mutation<SwitchShopResponse, { shopId?: string } | void>({
      query: (arg) => {
        let shopId: string | undefined | null = undefined;
        if (typeof arg === 'object' && arg !== null && 'shopId' in arg) {
          shopId = arg.shopId;
        }
        return {
          url: shopId ? `/v2/shops/switch?shop_id=${shopId}` : `/v2/shops/switch`,
          method: 'GET',
        };
      },
    }),
    getShopDetails: builder.query<IShopDetails, string>({
      query: (shopId) => ({
        url: `/v2/shops/${shopId}`,
        method: 'GET',
      }),
      providesTags: (result, error, shopId) => [{ type: 'Store', id: shopId }],
    }),
    // Запрос деталей магазина прямо к prod API (абсолютный URL)
    getShopDetailsProd: builder.query<IShopDetails, string>({
      query: (shopId) => ({
        url: `${PROD_API_URL}/v2/shops/${shopId}`,
        method: 'GET',
      }),
    }),
    updateShopDetails: builder.mutation<
      IShopDetails,
      { shopId: string; data: Partial<IShopDetails> }
    >({
      query: ({ shopId, data }) => ({
        url: `/v2/shops/${shopId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { shopId }) => [{ type: 'Store', id: shopId }],
    }),
    // Эндпоинт для добавления магазина
    createShopDetails: builder.mutation<IShopDetails, { data: Partial<IShopDetails> }>({
      query: ({ data }) => ({
        url: '/v2/shops',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { data }) => [
        { type: 'User', id: data.owner_id as string },
        { type: 'Shop', id: 'LIST' },
      ],
    }),
    // Эндпоинт для добавления организации магазина
    createShopOrganization: builder.mutation<
      void,
      { data: { organization_id: string; shop_id: string } }
    >({
      query: ({ data }) => ({
        url: '/v2/shops/organizations',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { data }) => [{ type: 'Store', id: data.shop_id as string }],
    }),
    // Получение API ключей
    getApiKeys: builder.query<IApiKey[], string>({
      query: (shopId) => `/v2/api-key?shop_id=${shopId}`,
      providesTags: (result, error, shopId) => [{ type: 'ApiKey', id: shopId }],
    }),
    // Создание API ключа
    createApiKey: builder.mutation<IApiKey, { shopId: string; expire: number }>({
      query: ({ shopId, expire }) => ({
        url: '/v2/api-key',
        method: 'POST',
        body: { shop_id: shopId, expire },
      }),
      invalidatesTags: (result, error, { shopId }) => [{ type: 'ApiKey', id: shopId }],
    }),
    // Удаление API ключа
    deleteApiKey: builder.mutation<void, { shopId: string; apiKeyId: string }>({
      query: ({ apiKeyId }) => ({
        url: `/v2/api-key/${apiKeyId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { shopId }) => [{ type: 'ApiKey', id: shopId }],
    }),

    // Экспорт магазинов
    getShopsExport: builder.query<Blob, GetShopsParams>({
      query: (params) => ({
        url: '/v2/shops/exports',
        method: 'GET',
        params,
        responseHandler: async (response) => response.blob(),
      }),
    }),

    // Загрузка лого магазина
    createShopLogo: builder.mutation<void, { shop_id: string; data: FormData }>({
      query: ({ shop_id, data }) => ({
        url: `/v2/shops/${shop_id}/logo`,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { shop_id }) => [
        { type: 'Store', id: shop_id },
        { type: 'Shop', id: 'LIST' },
      ],
    }),
    // Удалить лого магазина
    deleteShopLogo: builder.mutation<void, { shop_id: string }>({
      query: ({ shop_id }) => ({
        url: `/v2/shops/${shop_id}/logo`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, error, { shop_id }) => [
        { type: 'Store', id: shop_id },
        { type: 'Shop', id: 'LIST' },
      ],
    }),
    // Запуск фоновой синхронизации магазина из stage в prod
    syncShopFromStageToProd: builder.mutation<
      {
        status: string;
        detail: string;
      },
      { shop_id: string }
    >({
      query: ({ shop_id }) => ({
        url: '/v2/shops/sync',
        method: 'POST',
        params: { shop_id },
      }),
    }),
  }),
});

export const {
  useGetShopsQuery,
  useGetApiKeysQuery,
  useGetAllShopsQuery,
  useGetShopMailingListQuery,
  useGetCurrentShopsQuery,
  useCreateShopMailingMutation,
  useUpdateShopMailingMutation,
  useDeleteShopMailingMutation,
  useSwitchShopMutation,
  useGetShopDetailsQuery,
  useGetShopDetailsProdQuery,
  useLazyGetShopDetailsQuery,
  useCreateShopOrganizationMutation,
  useCreateApiKeyMutation,
  useDeleteApiKeyMutation,
  useUpdateShopDetailsMutation,
  useCreateShopDetailsMutation,
  useLazyGetShopsExportQuery,
  useCreateShopLogoMutation,
  useDeleteShopLogoMutation,
  useSyncShopFromStageToProdMutation,
} = shopApi;
